import {
  BrokerApplicationProcessSteps,
  BrokerProcessStep,
  BrokerProcessStepNames,
  StepStatus,
  type ActionType,
  type AppUser,
  type BrokerProcessStatus,
  type BrokerProcessStepEnum,
  type LoanApplication,
} from '@/types';
import { assign, createMachine } from 'xstate';
import { createLoanApp } from './builder';
import { ExitStrategyValue, LoanProductType, LoanType } from './const';
import { hasAssets } from './typeGuards';

export interface BrokerLoanAppMachineContext {
  loanApp: LoanApplication;
  from?: string;
  authorisedUser: Record<string, string>;
  // targetApplicant: ApplicantId;
  status: BrokerProcessStatus;
  current: BrokerProcessStepEnum;
}

export const initMachine = (params?: {
  actionType: ActionType;
  userType: AppUser;
  brokerId?: string;
}): BrokerLoanAppMachineContext => ({
  loanApp: createLoanApp(params),
  // targetApplicant: '',
  authorisedUser: {},
  status: Object.fromEntries(
    BrokerProcessStepNames.map((step) => [
      step,
      step === BrokerProcessStep.SelectLoanType ? StepStatus.Ready : StepStatus.Incomplete,
    ]),
  ) as BrokerProcessStatus,
  current: BrokerProcessStep.SelectLoanType,
});

export type BrokerLoanAppMachineEvent =
  | { type: 'NEXT'; loanApp: LoanApplication }
  | { type: 'UPDATE'; loanApp: LoanApplication }
  | { type: 'BACK' }
  | { type: 'READY' }
  | { type: 'GOTO'; step: BrokerProcessStepEnum }
  | { type: 'RELOAD'; state: BrokerLoanAppMachineContext }
  | { type: 'RESTART' }
  | { type: 'RESTART_BROKER'; brokerId: string };

export const loanApplicationMachineBroker = createMachine<BrokerLoanAppMachineContext, BrokerLoanAppMachineEvent>(
  {
    id: 'loanApplicationBroker',
    predictableActionArguments: true,
    schema: {
      context: {} as BrokerLoanAppMachineContext,
    },
    context: initMachine(),
    initial: 'start',
    states: {
      start: {
        on: {
          NEXT: {
            target: 'gotoReady',
          },
        },
      },
      [BrokerProcessStep.SelectLoanType]: {
        on: {
          NEXT: [
            {
              cond: (ctx, evt) =>
                evt.loanApp.loanType === LoanType.SINGLE_SECURITY || evt.loanApp.loanType === LoanType.PURCHASE_ONLY,
              target: 'checkPurpose',
              actions: 'saveData',
            },
            {
              target: BrokerProcessStep.PropertyDetails,
              actions: 'saveData',
            },
          ],
        },
      },
      checkPurpose: {
        always: [
          {
            target: BrokerProcessStep.PropertyDetails,
            cond: 'showLoanDetails',
          },
          {
            target: BrokerProcessStep.LoanPurpose,
          },
        ],
      },
      [BrokerProcessStep.LoanPurpose]: {
        on: {
          NEXT: {
            target: BrokerProcessStep.PropertyDetails,
            actions: 'saveData',
          },
        },
      },
      [BrokerProcessStep.PropertyDetails]: {
        on: {
          NEXT: [
            {
              cond: (ctx, evt) =>
                evt.loanApp.loanType === LoanType.SINGLE_SECURITY || evt.loanApp.loanType === LoanType.PURCHASE_ONLY,
              target: 'checkExitStrategy',
              actions: 'saveData',
            },
            {
              target: BrokerProcessStep.LoanDetails,
              actions: 'saveData',
            },
          ],
        },
      },
      checkExitStrategy: {
        always: [
          {
            target: BrokerProcessStep.ExitStrategy,
            cond: 'showExitStrategy',
          },
          {
            target: 'checkLoanDetails',
          },
        ],
      },
      [BrokerProcessStep.ExitStrategy]: {
        on: {
          NEXT: {
            target: BrokerProcessStep.ApplicantDetails,
            actions: 'saveData',
          },
        },
      },
      checkLoanDetails: {
        always: [
          {
            target: BrokerProcessStep.LoanDetails,
            cond: 'showLoanDetailsAfterPropertyExit',
          },
          {
            target: BrokerProcessStep.ApplicantDetails,
          },
        ],
      },
      [BrokerProcessStep.LoanDetails]: {
        on: {
          NEXT: {
            target: BrokerProcessStep.ApplicantDetails,
            actions: 'saveData',
          },
        },
      },
      [BrokerProcessStep.ApplicantDetails]: {
        on: {
          NEXT: {
            target: BrokerProcessStep.PropertyOwnership,
            actions: 'saveData',
          },
        },
      },
      [BrokerProcessStep.PropertyOwnership]: {
        on: {
          NEXT: {
            target: 'checkDebt',
            actions: 'saveData',
          },
        },
      },
      checkDebt: {
        always: [
          {
            target: BrokerProcessStep.Income,
            cond: 'showIncomeExpenses',
          },
          {
            target: BrokerProcessStep.Assets,
            cond: 'showAssets',
          },
          {
            target: BrokerProcessStep.SubmitApplication,
          },
        ],
      },
      [BrokerProcessStep.Income]: {
        on: {
          NEXT: {
            target: 'checkExpenses',
            actions: 'saveData',
          },
        },
      },
      checkExpenses: {
        always: [
          {
            target: BrokerProcessStep.Expenses,
            cond: 'showExpenses',
          },
          {
            target: BrokerProcessStep.Assets,
          },
        ],
      },
      [BrokerProcessStep.Expenses]: {
        on: {
          NEXT: {
            target: BrokerProcessStep.Assets,
            actions: 'saveData',
          },
        },
      },
      [BrokerProcessStep.Assets]: {
        on: {
          NEXT: [
            {
              cond: (ctx) =>
                ctx.loanApp.userType === 'Broker' ||
                !!ctx.loanApp.brokerId ||
                ctx.loanApp.actionType === 'updateExisting',
              target: BrokerProcessStep.SubmitApplication,
              actions: 'saveData',
            },
          ],
        },
      },
      [BrokerProcessStep.SubmitApplication]: {
        on: {
          NEXT: {
            target: BrokerProcessStep.Finished,
            actions: 'saveData',
          },
        },
      },
      [BrokerProcessStep.Finished]: {
        on: {
          NEXT: {
            target: BrokerProcessStep.SelectLoanType,
            actions: 'resetLoanApplication',
          },
        },
      },
      prevStage: {
        always: [...BrokerProcessStepNames]
          .sort((a, b) => BrokerApplicationProcessSteps[b]?.order - BrokerApplicationProcessSteps[a]?.order)
          .map((step) => ({
            target: step,
            cond: (ctx) =>
              BrokerApplicationProcessSteps[ctx.current].order > BrokerApplicationProcessSteps[step].order &&
              ctx.status[step] === StepStatus.Completed,
          })),
      },
      gotoReady: {
        always: BrokerProcessStepNames.map((step) => ({
          target: step,
          cond: (ctx) => ctx.status[step] === StepStatus.Ready,
        })),
      },
    },
    on: {
      BACK: {
        target: 'prevStage',
        cond: (ctx) => ctx.current !== BrokerProcessStep.SelectLoanType,
      },
      READY: {
        target: 'gotoReady',
      },
      UPDATE: {
        actions: 'saveData',
      },
      GOTO: BrokerProcessStepNames.map((step) => ({
        target: step,
        cond: (ctx, evt) => evt.step === step,
      })),
      RELOAD: {
        target: 'gotoReady',
        actions: 'updateContext',
      },
      RESTART: {
        target: BrokerProcessStep.SelectLoanType,
        actions: 'resetLoanApplication',
      },
      RESTART_BROKER: {
        target: BrokerProcessStep.SelectLoanType,
        actions: 'resetBrokerApplication',
      },
    },
  },
  {
    actions: {
      saveData: assign((context, event) => {
        if (event.type !== 'NEXT' && event.type !== 'UPDATE') {
          return {};
        }

        const currentStep = BrokerApplicationProcessSteps[context.current];
        if (!currentStep) {
          return { loanApp: event.loanApp };
        }

        const status = Object.fromEntries(
          Object.entries(context.status)
            .filter(([step]) => BrokerApplicationProcessSteps[step as BrokerProcessStepEnum])
            .map(([step, status]) => [
              step,
              step === context.current
                ? StepStatus.Completed
                : BrokerApplicationProcessSteps[step as BrokerProcessStepEnum].order < currentStep.order
                ? status
                : StepStatus.Incomplete,
            ]),
        ) as BrokerProcessStatus;

        return { loanApp: event.loanApp, status };
      }),
      updateContext: assign((context, event) => (event.type === 'RELOAD' ? event.state : {})),
      resetLoanApplication: assign(() => initMachine({ userType: 'Primary', actionType: 'createNew' })),
      resetBrokerApplication: assign((context, event) =>
        initMachine({
          userType: 'Broker',
          brokerId: event.type === 'RESTART_BROKER' ? event.brokerId : '',
          actionType: 'createNew',
        }),
      ),
    },
    guards: {
      hasEndDebt: (ctx) => ctx.loanApp.endDebt > 0,
      /**
       * Determines if Income (and potentially Expenses) steps should be shown.
       *
       * Rules:
       * - CRM: Always show
       * - End Debt > 0: Show income step (commercial and residential behave the same here;
       *   expenses may be skipped for unregulated, except commercial — see showExpenses below)
       * - Single Security + No End Debt + Exit=Sale: Skip (assets only scenario)
       * - No End Debt (other): Skip
       */
      showIncomeExpenses: (ctx) => {
        const isPurchaseOnly = ctx.loanApp.loanType === LoanType.PURCHASE_ONLY;
        const hasEndDebt = ctx.loanApp.endDebt > 0;
        const isSingleSecurity = ctx.loanApp.loanType === LoanType.SINGLE_SECURITY;
        const isFromCrm = ctx.from === 'crm';

        // For Purchase only loans, always show income/expenses
        // Since purchase only loan does not have end debt, it will always show income/expenses
        if (isPurchaseOnly && ctx.loanApp.exitStrategy === ExitStrategyValue.SECURITY_REFINANCE) return true;

        // CRM always shows income/expenses
        if (isFromCrm) return true;

        // No end debt scenarios: skip income/expenses
        // For Single Security + No End Debt + Exit=Sale: goes directly to assets (assets only scenario)
        // For all other no end debt cases: skip financial collection entirely
        if (!hasEndDebt) {
          return false;
        }

        // End debt scenarios: show income
        // For Single Security + End Debt + security refinance exit strategy
        if (isSingleSecurity && ctx.loanApp.exitStrategy === ExitStrategyValue.SECURITY_REFINANCE) {
          return true;
        }

        // For bridging loans with end debt: show income
        if (!isSingleSecurity) {
          return true;
        }

        // Single Security + End Debt + other exit strategies: still show income
        return hasEndDebt;
      },
      /**
       * Determines if Assets step should be shown.
       *
       * Rules:
       * - Single Security: Always show
       * - End Debt > 0: Show (comes after income/expenses)
       * - Single Security + No End Debt + Exit=Sale: Show (assets only scenario)
       * - No End Debt (other): Skip
       */
      showAssets: (ctx) => {
        const isSingleSecurityOrPurchaseOnly =
          ctx.loanApp.loanType === LoanType.SINGLE_SECURITY || ctx.loanApp.loanType === LoanType.PURCHASE_ONLY;
        const hasEndDebt = ctx.loanApp.endDebt > 0;
        // Single security always shows assets
        if (isSingleSecurityOrPurchaseOnly) return true;

        // End debt shows assets (reached via income/expenses path)
        if (hasEndDebt) return true;

        // Check if there are existing assets
        if (hasAssets(ctx.loanApp)) return true;

        return false;
      },
      hasNoBroker: (ctx) => ctx.loanApp.userType !== 'Broker',
      showLoanDetails: (ctx) => ctx.loanApp.loanType === LoanType.BRIDGING,
      /** Only reached from checkExitStrategy (single security or purchase-only). Purchase-only always continues to Loan Details; single security with no end debt goes to applicants. */
      showLoanDetailsAfterPropertyExit: (ctx) => ctx.loanApp.loanType === LoanType.PURCHASE_ONLY,
      showExitStrategy: (ctx) => ctx.loanApp.endDebt > 0 || ctx.loanApp.loanType === LoanType.PURCHASE_ONLY,
      /**
       * Determines if Expenses step should be shown.
       *
       * Rules:
       * - Commercial: Always show (this state is only reached via Income, which already
       *   gates commercial on end debt + an individual-type borrower being present)
       * - Unregulated: NEVER show expenses (skip to assets)
       * - Regulated + End Debt: Show expenses
       * - CRM: Always show
       */
      showExpenses: (ctx) => {
        const isFromCrm = ctx.from === 'crm';
        const isRegulated = ctx.loanApp.assessmentType !== 'unregulated';
        const isCommercial = ctx.loanApp.productType === LoanProductType.COMMERCIAL;

        // Commercial: expenses follow income's reachability regardless of regulated status
        if (isCommercial) return true;

        // CRM always shows expenses
        if (isFromCrm) return true;

        // Unregulated: skip expenses
        if (!isRegulated) return false;

        // Regulated: show expenses
        return true;
      },
    },
  },
);
